import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, Paper,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import BiotechIcon from '@mui/icons-material/Biotech';
import { useNavigate } from 'react-router-dom';
import { tssService } from '../services/api';
import { TssFermenter, TssReplicate, TssRemark, CreateTssEntryDto } from '../types';

// ── Constants ──────────────────────────────────────────────────────────────────

const FERMENTERS: TssFermenter[] = ['F1', 'F2', 'F3', 'IST'];
const REPLICATES: TssReplicate[] = ['R1', 'R2', 'R3'];
const TSS_LOW  = 15_000;
const TSS_HIGH = 60_000;

// ── Types ──────────────────────────────────────────────────────────────────────

interface RepInput { filter: string; contant: string; }
type FormState = Record<TssFermenter, Record<TssReplicate, RepInput>>;

const emptyForm = (): FormState =>
  Object.fromEntries(
    FERMENTERS.map((f) => [
      f,
      Object.fromEntries(REPLICATES.map((r) => [r, { filter: '', contant: '' }])),
    ]),
  ) as FormState;

// ── Calc helpers ───────────────────────────────────────────────────────────────

const parseSafe = (v: string): number | null => {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};

const calcTss = (filter: string, contant: string): number | null => {
  const f = parseSafe(filter);
  const c = parseSafe(contant);
  if (f == null || c == null) return null;
  return ((c - f) * 1000 / 100) * 400;
};

const calcAvg = (values: (number | null)[]): number | null => {
  const valid = values.filter((v): v is number => v != null);
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
};

const calcRemark = (avg: number | null): TssRemark | null => {
  if (avg == null) return null;
  if (avg < TSS_LOW)  return 'Below required level';
  if (avg > TSS_HIGH) return 'Above range';
  return 'Stable';
};

const remarkColor = (r: TssRemark | null): string => {
  if (r === 'Stable') return '#2e7d32';
  if (r === 'Above range') return '#e65100';
  if (r === 'Below required level') return '#c62828';
  return 'inherit';
};

const fmt = (v: number | null, dp = 2): string =>
  v == null ? '' : v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

// ── Header Styles ──────────────────────────────────────────────────────────────

const TH_STYLE = {
  bgcolor: '#263238',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.72rem',
  border: '1px solid #455a64',
  whiteSpace: 'nowrap',
  px: 1,
  py: 0.75,
} as const;

const TD_STYLE = {
  border: '1px solid #cfd8dc',
  fontSize: '0.72rem',
  px: 1,
  py: 0.5,
} as const;

const FERM_COLORS: Record<TssFermenter, string> = {
  F1:  '#e8f5e9',
  F2:  '#e3f2fd',
  F3:  '#f3e5f5',
  IST: '#fff3e0',
};

const FERM_HDR_COLORS: Record<TssFermenter, string> = {
  F1:  '#2e7d32',
  F2:  '#1565c0',
  F3:  '#6a1b9a',
  IST: '#e65100',
};

// ── Component ──────────────────────────────────────────────────────────────────

const TSSDigestateForm: React.FC = () => {
  const navigate  = useNavigate();
  const today     = new Date().toISOString().split('T')[0];
  const [date, setDate]           = useState<string>(today);
  const [form, setForm]           = useState<FormState>(emptyForm());
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [snack, setSnack]         = useState<string | null>(null);

  // ── Derived calculations ──────────────────────────────────────────────────

  const calc = useMemo(() => {
    return FERMENTERS.reduce((acc, ferm) => {
      const repTss = REPLICATES.reduce((ra, rep) => {
        ra[rep] = calcTss(form[ferm][rep].filter, form[ferm][rep].contant);
        return ra;
      }, {} as Record<TssReplicate, number | null>);

      const avg    = calcAvg(Object.values(repTss));
      const remark = calcRemark(avg);
      const pct    = avg != null ? avg / 10_000 : null;

      acc[ferm] = { repTss, avg, remark, pct };
      return acc;
    }, {} as Record<TssFermenter, {
      repTss: Record<TssReplicate, number | null>;
      avg: number | null;
      remark: TssRemark | null;
      pct: number | null;
    }>);
  }, [form]);

  // ── Load existing data ────────────────────────────────────────────────────

  const loadData = useCallback(async (d: string) => {
    setLoading(true);
    setError(null);
    try {
      const report = await tssService.getDailyReport(d);
      const next = emptyForm();
      for (const fResult of report.fermenters) {
        const ferm = fResult.fermenter as TssFermenter;
        for (const rResult of fResult.replicates) {
          const rep = rResult.replicate as TssReplicate;
          next[ferm][rep] = {
            filter:  rResult.filterDiscWeightMg != null ? String(rResult.filterDiscWeightMg) : '',
            contant: rResult.contantWeightMg    != null ? String(rResult.contantWeightMg)    : '',
          };
        }
      }
      setForm(next);
    } catch {
      // No data for this date — start with empty form, not an error
      setForm(emptyForm());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(date); }, [date, loadData]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleInput = (ferm: TssFermenter, rep: TssReplicate, field: 'filter' | 'contant', value: string) => {
    setForm((prev) => ({
      ...prev,
      [ferm]: { ...prev[ferm], [rep]: { ...prev[ferm][rep], [field]: value } },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const entries: CreateTssEntryDto[] = [];
      for (const ferm of FERMENTERS) {
        for (const rep of REPLICATES) {
          entries.push({
            measurementDate:    date,
            fermenter:          ferm,
            replicate:          rep,
            filterDiscWeightMg: parseSafe(form[ferm][rep].filter),
            contantWeightMg:    parseSafe(form[ferm][rep].contant),
          });
        }
      }
      await tssService.upsertBatch(entries);
      setSnack('Saved successfully');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete all TSS data for ${date}?`)) return;
    try {
      await tssService.deleteByDate(date);
      setForm(emptyForm());
      setSnack('Data cleared');
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const totalRows = FERMENTERS.length * REPLICATES.length; // 12

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* ── Page header ── */}
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2, mb: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/lab-report')} sx={{ color: '#90a4ae' }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <BiotechIcon sx={{ fontSize: '2.2rem', opacity: 0.8 }} />
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.7, display: 'block', lineHeight: 1, letterSpacing: 2 }}>
              POLOMOLOK
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Total Suspended Solids — Digestate
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Measurement Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true, style: { color: 'rgba(255,255,255,0.7)' } }}
            inputProps={{ style: { color: '#fff' } }}
            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' } }}
          />
          <Tooltip title="Delete all entries for this date">
            <IconButton onClick={handleDelete} sx={{ color: '#ef9a9a' }}>
              <DeleteSweepIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={() => window.print()} sx={{ color: '#fff' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' }, fontWeight: 700 }}
          >
            Save
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Print header ── */}
          <Box className="print-only" sx={{ display: 'none', textAlign: 'center', mb: 1 }}>
            <Typography variant="h5" fontWeight={700}>POLOMOLOK</Typography>
            <Typography variant="subtitle1">Total Suspended Solids — Digestate</Typography>
            <Typography variant="body2">Date: {date}</Typography>
          </Box>

          {/* ── Target info banner ── */}
          <Box sx={{ mb: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2e7d32' }} />
              <Typography variant="caption" sx={{ color: '#555' }}>
                <strong>Stable</strong> — 15,000–60,000 ppm
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e65100' }} />
              <Typography variant="caption" sx={{ color: '#555' }}>
                <strong>Above range</strong> — &gt; 60,000 ppm
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#c62828' }} />
              <Typography variant="caption" sx={{ color: '#555' }}>
                <strong>Below required level</strong> — &lt; 15,000 ppm
              </Typography>
            </Box>
          </Box>

          {/* ── Main table ── */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small" sx={{ borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 90 }}>Date</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 50 }}>Fermenter</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 50 }}>Replicate #</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 110 }} align="right">Filter disc,{'\n'}Weight (mg)</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 110 }} align="right">Contant{'\n'}Weight (mg)</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 90 }} align="right">TSS, ppm</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 100 }} align="right">Average{'\n'}TSS, ppm</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 120 }} align="center">Target Range</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 130 }} align="center">Remarks</TableCell>
                  <TableCell sx={{ ...TH_STYLE, minWidth: 60 }} align="right">%TSS</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {FERMENTERS.map((ferm, fi) =>
                  REPLICATES.map((rep, ri) => {
                    const isFirstRep  = ri === 0;
                    const isFirstFerm = fi === 0 && ri === 0;
                    const tss         = calc[ferm].repTss[rep];
                    const { avg, remark, pct } = calc[ferm];

                    return (
                      <TableRow key={`${ferm}-${rep}`}>
                        {/* Date — rowSpan all 12 rows */}
                        {isFirstFerm && (
                          <TableCell
                            rowSpan={totalRows}
                            align="center"
                            sx={{
                              ...TD_STYLE,
                              fontWeight: 700,
                              bgcolor: '#eceff1',
                              fontSize: '0.68rem',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {date}
                          </TableCell>
                        )}

                        {/* Fermenter label — rowSpan 3 */}
                        {isFirstRep && (
                          <TableCell
                            rowSpan={REPLICATES.length}
                            align="center"
                            sx={{
                              ...TD_STYLE,
                              fontWeight: 700,
                              bgcolor: FERM_COLORS[ferm],
                              color: FERM_HDR_COLORS[ferm],
                              fontSize: '0.78rem',
                              verticalAlign: 'middle',
                            }}
                          >
                            {ferm}
                          </TableCell>
                        )}

                        {/* Replicate */}
                        <TableCell align="center" sx={{ ...TD_STYLE, bgcolor: '#f5f5f5' }}>
                          {rep}
                        </TableCell>

                        {/* Inputs */}
                        <TableCell align="right" sx={{ ...TD_STYLE, bgcolor: '#fafafa', p: 0.25, minWidth: 90 }}>
                          <TextField
                            size="small"
                            value={form[ferm][rep].filter}
                            onChange={(e) => handleInput(ferm, rep, 'filter', e.target.value)}
                            inputProps={{
                              style: { fontSize: '0.72rem', textAlign: 'right', padding: '2px 4px' },
                              step: 0.001,
                            }}
                            type="number"
                            variant="standard"
                            sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottomColor: '#90a4ae' } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ ...TD_STYLE, bgcolor: '#fafafa', p: 0.25, minWidth: 90 }}>
                          <TextField
                            size="small"
                            value={form[ferm][rep].contant}
                            onChange={(e) => handleInput(ferm, rep, 'contant', e.target.value)}
                            inputProps={{
                              style: { fontSize: '0.72rem', textAlign: 'right', padding: '2px 4px' },
                              step: 0.001,
                            }}
                            type="number"
                            variant="standard"
                            sx={{ width: '100%', '& .MuiInput-underline:before': { borderBottomColor: '#90a4ae' } }}
                          />
                        </TableCell>

                        {/* TSS per replicate — calculated */}
                        <TableCell
                          align="right"
                          sx={{
                            ...TD_STYLE,
                            bgcolor: '#f9fbe7',
                            fontWeight: tss != null ? 500 : 400,
                            color: tss != null ? '#33691e' : '#ccc',
                          }}
                        >
                          {tss != null ? fmt(tss, 0) : '—'}
                        </TableCell>

                        {/* Average TSS — rowSpan 3, shows once per fermenter */}
                        {isFirstRep && (
                          <TableCell
                            rowSpan={REPLICATES.length}
                            align="right"
                            sx={{
                              ...TD_STYLE,
                              bgcolor: FERM_COLORS[ferm],
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              verticalAlign: 'middle',
                              color: avg != null ? FERM_HDR_COLORS[ferm] : '#bbb',
                            }}
                          >
                            {avg != null ? fmt(avg, 0) : '—'}
                          </TableCell>
                        )}

                        {/* Target Range — rowSpan 3 */}
                        {isFirstRep && (
                          <TableCell
                            rowSpan={REPLICATES.length}
                            align="center"
                            sx={{
                              ...TD_STYLE,
                              bgcolor: '#eceff1',
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              verticalAlign: 'middle',
                              color: '#455a64',
                            }}
                          >
                            15,000–60,000 ppm
                          </TableCell>
                        )}

                        {/* Remarks — rowSpan 3 */}
                        {isFirstRep && (
                          <TableCell
                            rowSpan={REPLICATES.length}
                            align="center"
                            sx={{
                              ...TD_STYLE,
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              verticalAlign: 'middle',
                              color: remarkColor(remark),
                              bgcolor: remark === 'Stable'
                                ? '#f1f8f1'
                                : remark != null ? '#fff9f9' : '#fafafa',
                            }}
                          >
                            {remark ?? '—'}
                          </TableCell>
                        )}

                        {/* %TSS — rowSpan 3 */}
                        {isFirstRep && (
                          <TableCell
                            rowSpan={REPLICATES.length}
                            align="right"
                            sx={{
                              ...TD_STYLE,
                              fontWeight: 600,
                              verticalAlign: 'middle',
                              bgcolor: FERM_COLORS[ferm],
                              color: pct != null ? FERM_HDR_COLORS[ferm] : '#bbb',
                            }}
                          >
                            {pct != null ? fmt(pct, 2) : '—'}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Interpretation ── */}
          <Paper
            variant="outlined"
            sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#fafafa' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Interpretation:
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Stable:</strong> "TSS is typical for digestate, indicating within range separation/settling."
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Above range:</strong> "TSS too high, poor solid–liquid separation; risk for handling."
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Below range:</strong> "TSS too low, may indicate over-dilution or sampling from supernatant."
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#888' }}>
              Formula: TSS (ppm) = (((Contant Weight − Filter disc Weight) × 1,000) / 100) × 400 &nbsp;|&nbsp;
              %TSS = Average TSS (ppm) / 10,000
            </Typography>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
        }
      `}</style>
    </Box>
  );
};

export default TSSDigestateForm;
